import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { QueryParams } from '../api.service';
import {
  RealtimeConnection,
  RealtimeEventHandler,
  RealtimeMessage,
  RealtimeService,
} from '../realtime.service';
import { StorageAPIService } from '../storage-api.service';
import { ObjectLabel, Sideloadable } from './object-label.service';

export interface Calculation {
  id: string;
  request: CalculationRequest;
  result: CalculationResult;
  status: 'PENDING' | 'RUNNING' | 'COMPLETE' | 'ERROR';
  createdAt: Date;
  finishedAt: Date;
  collectionId: string;
  taskId?: string;
  tags?: string[];
  objectLabels?: string[] | ObjectLabel[];
}

export interface CalculationRequest {
  dependentColumn: string;
  dependentColumnLabel?: string;
  driverColumns: string[];
  driverColumnLabels?: string[];
  breakdownColumn?: string;
  breakdownColumnLabel?: string;
  displayName: string;
  tags?: string[];
  properties: CalculationRequestProperties;
}

export interface CalculationRequestProperties {
  ['dashboard:filters']?: string;
  ['dashboard:comparisonFilters']?: string;
  ['dashboard:widgetGroupFilters']?: string;
  userSearch?: string;
  weightField?: string;
  query?: string;
  analysisId?: string;
  timezone?: string;
  [key: string]: string;
}

export interface ResultColumn {
  average: number;
  confidence?: number;
  importance: number;
  rescaledRelativeWeights: number;
  label: string;
}

export interface ResultGroup {
  average: number;
  count: number;
  drivers: ResultColumn[];
}

export interface CalculationResult {
  breakdown: { [key: string]: ResultGroup };
  id: string;
  message: string;
  topline: ResultGroup;
}

//General calculation data
export interface CalculationListItem {
  id: string;
  displayName: string;
  collectionId: string;
  status: string;
  createdAt: string;
  finishedAt: string;
  //If there are tags, it is a new KDA type (KDAS created from Collection Analyses don’t' have tag array)
  tags?: string[];
  documentCount?: number;
  query?: string;
  objectLabels?: string[] | ObjectLabel[];
}

export interface CalculationListResponse {
  count: number;
  items: CalculationListItem[];
}

export interface CalculationListOptions {
  page?: number;
  pageSize?: number;
  sortField?: 'createdAt' | 'finishedAt' | 'status';
  sortOrder?: 'DESC' | 'ASC';
  status?: string;
  displayName?: string;
  tags?: string[];
  sideloads?: 'objectLabels';
}

@EnableClassTelemetry()
@Injectable()
export class PredictiveService extends StorageAPIService {
  static mapListItem(item: CalculationListItem, labels?: ObjectLabel[]): CalculationListItem {
    item.objectLabels = this.mapObjectLabels(item, labels);

    return item;
  }

  static mapItem(item: Calculation, labels?: ObjectLabel[]): Calculation {
    item.objectLabels = this.mapObjectLabels(item, labels);

    return item;
  }

  constructor(
    http: HttpClient,
    systemService: SystemService,
    private realtimeService: RealtimeService
  ) {
    super(http, systemService);
  }

  on(namespace: string, fn: RealtimeEventHandler<CalculationListItem>): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        driverAnalysis: (data: RealtimeMessage<CalculationListItem>) => {
          PredictiveService.mapListItem(data.object);
          fn(data);
        },
      },
      'predictiveService'
    );
  }

  createDriverAnalysis(projectId, collectionId, dto: CalculationRequest) {
    const displayName = dto.displayName;
    const tags = dto.tags;

    delete dto.displayName;
    delete dto.tags;

    return this.post(`/projects/${projectId}/collections/${collectionId}/driver-analysis`, {
      displayName,
      tags,
      request: dto,
    });
  }

  //Get general calculation data for collection
  listCalculations(
    projectId: string,
    collectionId: string,
    options?: CalculationListOptions
  ): Promise<CalculationListResponse> {
    if (!options) {
      options = {};
    }
    return this.get<Sideloadable<CalculationListResponse>>(
      `/projects/${projectId}/collections/${collectionId}/driver-analysis`,
      options as QueryParams
    ).then((response) => {
      if (response.items) {
        response.items = response.items.map((c) =>
          PredictiveService.mapListItem(c, response.sideloaded?.objectLabels)
        );
      }
      return response;
    });
  }

  //Get detailed calcuation data for NEW KDAs
  getCalculationById(
    projectId: string,
    collectionId: string,
    calcId: string,
    params?: QueryParams
  ): Promise<Calculation> {
    return this.post<Sideloadable<Calculation>>(
      `/projects/${projectId}/collections/${collectionId}/driver-analysis/${calcId}`,
      params
    ).then((res) => PredictiveService.mapItem(res, res.sideloaded?.objectLabels));
  }

  deleteCalculationById(projectId: string, collectionId: string, calcId: string): Promise<void> {
    return this.delete<void>(
      `/projects/${projectId}/collections/${collectionId}/driver-analysis/${calcId}`
    );
  }

  updateDriverAnalysis(
    projectId: string,
    collectionId: string,
    analysisId: string,
    displayName: string,
    notes: string
  ) {
    const params = { notes, displayName };
    return this.put(
      `/projects/${projectId}/collections/${collectionId}/driver-analysis/${analysisId}`,
      params
    );
  }

  // THESE ARE BEING KEPT FOR BACK-COMPATIBLITY.  Should be removed eventually
  getOldCalculation(
    projectId: string,
    collectionId: string,
    params?: QueryParams
  ): Promise<Calculation> {
    // this used to be get by collection.  for now we'll do a list and take the calculation
    // without an explicit version (should be only one)
    return this.listCalculations(projectId, collectionId, { pageSize: 1024 }).then((response) => {
      if (response && response.items.length > 0) {
        const oldCalculation = response.items.find((c) => !c.tags || !c.tags.length);
        return this.getCalculationById(projectId, collectionId, oldCalculation.id, params);
      }
    });
  }
}
